import type { ActivityReportTopThread } from '../apis/observerApi';

export type ObserverThreadType = 'ticket_assistant' | 'deep_research' | 'chat_assistant';

/**
 * Row shape compatible with AI Thread Observer action handlers (messages, audit, usage, open ticket).
 * Activity Report top threads can be mapped into this shape.
 */
export interface ObserverThreadActionRow {
  ai_thread_id: number;
  thread_user_id: string | null;
  display_user_id: string | null;
  ticket_id: string | null;
  research_enabled?: boolean | number | string | null;
  thread_type?: ObserverThreadType | null;
  thread_name: string | null;
  msp_id?: number | null;
  pinned: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string | null;
  message_count: number;
  last_message_at: string | null;
  first_message_at: string | null;
}

export function activityReportTopThreadToActionRow(
  t: ActivityReportTopThread,
): ObserverThreadActionRow {
  const ticket = t.ticket_id != null && String(t.ticket_id).trim() !== ''
    ? String(t.ticket_id).trim()
    : null;
  return {
    ai_thread_id: t.ai_thread_id,
    thread_user_id: null,
    display_user_id: t.user_email?.trim() || null,
    ticket_id: ticket,
    research_enabled: t.research_enabled,
    thread_type: t.thread_type,
    thread_name: t.thread_name,
    pinned: false,
    is_deleted: false,
    created_at: t.last_message_at || '',
    updated_at: t.last_message_at,
    message_count: t.msgs_on_day,
    last_message_at: t.last_message_at,
    first_message_at: null,
  };
}

function isResearchEnabled(value: ObserverThreadActionRow['research_enabled']): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return ['1', 'true', 'yes'].includes(value.trim().toLowerCase());
  return false;
}

export function observerThreadUserExperienceUrl(thread: ObserverThreadActionRow): string | null {
  if (!thread || thread.is_deleted) return null;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const ticketId = thread.ticket_id != null && String(thread.ticket_id).trim() !== ''
    ? String(thread.ticket_id).trim()
    : null;
  if (ticketId) {
    return `${origin}/supervisor/tickets/${encodeURIComponent(ticketId)}`;
  }
  const threadId = thread.ai_thread_id != null ? String(thread.ai_thread_id).trim() : '';
  if (!threadId) return null;
  const isDeepResearch = thread.thread_type === 'deep_research' || isResearchEnabled(thread.research_enabled);
  const basePath = isDeepResearch ? '/deep-research' : '/chat-assistant';
  return `${origin}${basePath}/${encodeURIComponent(threadId)}`;
}

export function observerThreadUserExperienceTitle(thread: ObserverThreadActionRow): string {
  if (thread.ticket_id) return 'Open Ticket';
  if (thread.thread_type === 'deep_research' || isResearchEnabled(thread.research_enabled)) {
    return 'Open Deep Research';
  }
  return 'Open Chat Agent';
}
